// `GET /api/v1/admin/me/capabilities` of CS (ADR 0031 section 2 of credlap-architecture;
// `CapabilitiesController`, `CapabilitiesView`). Roles never travel here: a feature is usable when
// its capability is available AND the role allows it (role-tiers).

/**
 * `ENABLED`: deployed and open. `PAUSED`: deployed and readable, its inbound commands stopped by a
 * hot switch. `DISABLED`: the deployment property is off, the feature does not exist here.
 */
export type CapabilityState = 'ENABLED' | 'PAUSED' | 'DISABLED';

export interface CapabilityView {
  key: string;
  state: CapabilityState;
  /** Optional operator text (never personal data). */
  reason?: string | null;
}

export interface CapabilitiesResponse {
  /** The revision of CS operational settings; changes with every hot switch. */
  revision: number;
  capabilities: CapabilityView[];
}

/** The keys AD knows today (ADR 0031 section 2.4). A key CS does not publish reads as DISABLED. */
export const CAPABILITY_KEYS = {
  /** Requirement 1.21, decision 12: the web's opt-out requests and the "Pedidos de baja" queue (OPS). */
  PUBLIC_OPT_OUT: 'stage1.public-opt-out',
  /**
   * Requirements 1.20 and 1.28, decision 13 (S40): the person stage computed from the loans, its
   * stage filter in Personas and the BCRA tranche column and filter.
   */
  LOAN_STAGE: 'stage1.loan-stage',
  DOCUMENT_MANAGER: 'stage1.document-manager',
  SB_IMPORT: 'stage1.sb-import',
  LEGAJO_WHY: 'stage1.legajo-why',
  CUSTOM_FIELDS: 'stage1.custom-fields',
  COMERCIALIZADORES: 'stage1.comercializadores',
  MESSAGING_MONITOR: 'messaging.monitor',
  CLIENT_EXPANSION: 'stage1.client-expansion',
  LOAN_ORIGINATION: 'stage1.loan-origination',
  CREDIT_LINES: 'stage1.credit-lines',
  DUPLICATES: 'stage1.duplicates',
  LOANS: 'stage1.loans',
  LOAN_EXCEPTIONS: 'stage1.loan-exceptions',
  /** Requirement 1.33: the "No disponible" reason (catalog screen, Personas column and filter). */
  UNAVAILABLE_REASON: 'stage1.unavailable-reason',
  /** Requirements 1.5, 1.7, 1.12: change history, "Guardar cambios" and removal with a free reason. */
  FICHA_EDITION: 'stage1.ficha-edition',
  /** Requirements 1.17 and 1.22: the origin catalog, corrections from it and the metrics by origin. */
  PERSON_ORIGIN: 'stage1.person-origin',
  /**
   * Requirements 4.4, 4.6, 4.7 and 4.8 (U10): repeat or skip an automatic control, the reason of a bulk
   * re-evaluation, every failed rule of a rejection and the extended exception report.
   */
  EXCEPTION_CONTROLS: 'stage1.exception-controls',
  /** Requirement 1.15 (partial): bulk BLOCKED mark of the selected people in Personas (ADMIN). */
  PERSON_BULK_MARKS: 'stage1.person-bulk-marks',
  /** Requirements 1.26 and 1.17: the person import and its identity review queue ("Cola de identidad", ADMIN). */
  PERSON_IMPORT: 'stage1.person-import',
} as const;

export type CapabilityKey = (typeof CAPABILITY_KEYS)[keyof typeof CAPABILITY_KEYS];
